import { useState, useEffect } from 'react';

const useModalEmprestimoUpdateDadosCadastrais = ({
    showLoanModal,
    setShowLoanModal,
    loanData,
    loadingLoanData,
    handleLoanFormSubmit,
}) => {
    const [formData, setFormData] = useState({
        nuProposal: '',
        takerRegister: {
            customerName: '',
            motherName: '',
            fatherName: '',
            gender: '',
            idNumber: '',
            emissionEntity: '',
            emissionDate: '',
            emissionState: '',
            civilStatus: '',
            birthplace: '',
            nationality: '',
        },
        takerData: {
            workPlaceName: '',
            workRoleName: '',
        },
        takerAddress: {
            zipCode: '',
            address: '',
            addressNumber: '',
            complement: '',
            neighborhood: '',
            city: '',
            state: '',
        },
        takerBankData: {
            accountData: {
                bankCode: '',
                agencyNumber: '',
                accountNumber: '',
                accountDigit: '',
                accountType: '',
            },
            pixData: {
                keyType: '',
                pixKey: '',
            },
        },
        nuEmployeeCPF: '',
    });

    // State to track which bank data type is selected
    const [bankDataType, setBankDataType] = useState('account'); // 'account' or 'pix'

    // Date conversion functions
    const convertDateToISO = (brazilianDate) => {
        if (!brazilianDate) return '';
        // If it's already in ISO format, return as is
        if (brazilianDate.includes('-')) return brazilianDate;
        // Convert from DD/MM/YYYY to YYYY-MM-DD
        const parts = brazilianDate.split('/');
        if (parts.length === 3) {
            return `${parts[2]}-${parts[1].padStart(2, '0')}-${parts[0].padStart(2, '0')}`;
        }
        return brazilianDate;
    };

    const convertDateToBrazilian = (isoDate) => {
        if (!isoDate) return '';
        // If it's already in Brazilian format, return as is
        if (isoDate.includes('/')) return isoDate;
        // Convert from YYYY-MM-DD to DD/MM/YYYY
        const parts = isoDate.split('-');
        if (parts.length === 3) {
            return `${parts[2]}/${parts[1]}/${parts[0]}`;
        }
        return isoDate;
    };

    // Initialize form data when modal opens
    useEffect(() => {
        if (showLoanModal && loanData) {
            setFormData({
                nuProposal: loanData.nuProposal || '',
                takerRegister: {
                    customerName: loanData.takerRegister?.customerName || '',
                    motherName: loanData.takerRegister?.motherName || '',
                    fatherName: loanData.takerRegister?.fatherName || '',
                    gender: loanData.takerRegister?.gender || '',
                    idNumber: loanData.takerRegister?.idNumber || '',
                    emissionEntity: loanData.takerRegister?.emissionEntity || '',
                    emissionDate: convertDateToISO(loanData.takerRegister?.emissionDate || ''),
                    emissionState: loanData.takerRegister?.emissionState || '',
                    civilStatus: loanData.takerRegister?.civilStatus || '',
                    birthplace: loanData.takerRegister?.birthplace || '',
                    nationality: loanData.takerRegister?.nationality || '',
                },
                takerData: {
                    workPlaceName: loanData.takerData?.workPlaceName || '',
                    workRoleName: loanData.takerData?.workRoleName || '',
                },
                takerAddress: {
                    zipCode: loanData.takerAddress?.zipCode || '',
                    address: loanData.takerAddress?.address || '',
                    addressNumber: loanData.takerAddress?.addressNumber || '',
                    complement: loanData.takerAddress?.complement || '',
                    neighborhood: loanData.takerAddress?.neighborhood || '',
                    city: loanData.takerAddress?.city || '',
                    state: loanData.takerAddress?.state || '',
                },
                takerBankData: {
                    accountData: {
                        bankCode: loanData.takerBankData?.accountData?.bankCode || '',
                        agencyNumber: loanData.takerBankData?.accountData?.agencyNumber || '',
                        accountNumber: loanData.takerBankData?.accountData?.accountNumber || '',
                        accountDigit: loanData.takerBankData?.accountData?.accountDigit || '',
                        accountType: loanData.takerBankData?.accountData?.accountType || '',
                    },
                    pixData: loanData.takerBankData?.pixData || {
                        keyType: '',
                        pixKey: '',
                    },
                },
                nuEmployeeCPF: loanData.nuEmployeeCPF || '',
            });
        }
    }, [showLoanModal, loanData]);

    const handleInputChange = (section, field, value) => {
        setFormData((prev) => ({
            ...prev,
            [section]: {
                ...prev[section],
                [field]: value,
            },
        }));
    };

    const handleNestedInputChange = (section, subsection, field, value) => {
        setFormData((prev) => ({
            ...prev,
            [section]: {
                ...prev[section],
                [subsection]: {
                    ...prev[section][subsection],
                    [field]: value,
                },
            },
        }));
    };

    // Specialized handler for ID Number with validation
    const handleIdNumberChange = (value) => {
        // Limit to 15 characters and only allow alphanumeric, dots, and hyphens
        const cleanedValue = value.slice(0, 15).replace(/[^a-zA-Z0-9.-]/g, '');
        handleInputChange('takerRegister', 'idNumber', cleanedValue);
    };

    const handleSubmit = (e) => {
        e.preventDefault();

        // Validate ID Number (RG/Documento) - max 15 characters and only alphanumeric
        const idNumber = formData.takerRegister.idNumber.trim();
        if (idNumber.length === 0) {
            alert('O número do documento é obrigatório.');
            return;
        }
        if (idNumber.length > 15) {
            alert('O número do documento não pode ter mais de 15 caracteres.');
            return;
        }
        // Only allow alphanumeric characters, dots, and hyphens (common in Brazilian documents)
        const allowedIdPattern = /^[a-zA-Z0-9.-]+$/;
        if (!allowedIdPattern.test(idNumber)) {
            alert(
                'O número do documento contém caracteres não permitidos. Use apenas letras, números, pontos e hífens.',
            );
            return;
        }

        // Validate ZIP Code - must be 8 digits only
        const zipCode = formData.takerAddress.zipCode.replace(/\D/g, ''); // Remove non-digits
        if (zipCode.length !== 8) {
            alert('CEP deve conter exatamente 8 dígitos.');
            return;
        }

        // Determine if we use PIX or account data based on user selection
        const usePix = bankDataType === 'pix';

        // Validate based on selected bank data type
        if (usePix) {
            const pixKeyType = formData.takerBankData.pixData?.keyType;
            const pixKey = formData.takerBankData.pixData?.pixKey;

            if (!pixKeyType || !pixKey) {
                alert('Para dados PIX, tanto o tipo quanto a chave são obrigatórios.');
                return;
            }
        }

        // Prepare cleaned bank data variables
        let cleanedAccountData = {};

        // If not using PIX, validate and clean account data
        if (!usePix) {
            const bankCode = formData.takerBankData.accountData.bankCode.replace(/\D/g, '');
            if (bankCode.length !== 3) {
                alert('Código do banco deve conter exatamente 3 dígitos.');
                return;
            }

            const agencyNumber = formData.takerBankData.accountData.agencyNumber.replace(/\D/g, '');
            if (agencyNumber.length !== 4) {
                alert('Agência deve conter exatamente 4 dígitos.');
                return;
            }

            const accountNumber = formData.takerBankData.accountData.accountNumber.replace(/\D/g, '');
            if (accountNumber.length < 4 || accountNumber.length > 13) {
                alert('Número da conta deve conter entre 4 e 13 dígitos.');
                return;
            }

            const accountDigit = formData.takerBankData.accountData.accountDigit.replace(/\D/g, '');
            if (accountDigit.length < 1 || accountDigit.length > 2) {
                alert('Dígito da conta deve conter 1 ou 2 dígitos.');
                return;
            }

            // Store cleaned data
            cleanedAccountData = {
                bankCode: bankCode,
                agencyNumber: agencyNumber,
                accountNumber: accountNumber,
                accountDigit: accountDigit,
                accountType: formData.takerBankData.accountData.accountType,
            };
        }

        const dataToSubmit = {
            ...formData,
            takerRegister: {
                ...formData.takerRegister,
                idNumber: idNumber, // Use validated and trimmed ID number
                emissionDate: convertDateToBrazilian(formData.takerRegister.emissionDate),
            },
            takerAddress: {
                ...formData.takerAddress,
                zipCode: zipCode, // Clean ZIP code
            },
            takerBankData: usePix
                ? {
                      pixData: {
                          keyType: formData.takerBankData.pixData.keyType,
                          pixKey: formData.takerBankData.pixData.pixKey,
                      },
                      accountData: null, // Set accountData to null when PIX is chosen
                  }
                : {
                      accountData: cleanedAccountData,
                      pixData: null, // Set pixData to null when accountData is chosen
                  },
        };

        handleLoanFormSubmit(dataToSubmit);
    };

    const brazilianStates = [
        'AC',
        'AL',
        'AP',
        'AM',
        'BA',
        'CE',
        'DF',
        'ES',
        'GO',
        'MA',
        'MT',
        'MS',
        'MG',
        'PA',
        'PB',
        'PR',
        'PE',
        'PI',
        'RJ',
        'RN',
        'RS',
        'RO',
        'RR',
        'SC',
        'SP',
        'SE',
        'TO',
    ];

    const genderOptions = [
        { value: 'M', label: 'Masculino' },
        { value: 'F', label: 'Feminino' },
    ];

    const civilStatusOptions = ['Solteiro', 'Casado', 'Separado', 'Divorciado', 'Viúvo'];

    const accountTypeOptions = [
        { value: '1', label: 'Conta Corrente' },
        { value: '2', label: 'Conta Poupança' },
    ];

    return {
        formData,
        setFormData,
        bankDataType,
        setBankDataType,
        handleInputChange,
        handleNestedInputChange,
        handleIdNumberChange,
        handleSubmit,
        brazilianStates,
        genderOptions,
        civilStatusOptions,
        accountTypeOptions,
    };
};

export default useModalEmprestimoUpdateDadosCadastrais;
